/**
 * @fileoverview Hook UI Layer para gestión visual y handlers de cambio de estado de orden de cliente en Inbox.
 * @module useCustomerOrderStatusUI
 * @category Hook - UI Layer
 * @since 1.0.0
 * @version 2.0.0
 */
import { useCallback, useMemo, useRef, useState } from "react";
import { useTranslation } from 'react-i18next';

/**
 * Hook UI Layer para CustomerOrderStatusSelectorMC.
 * Maneja estado visual, textos, handlers y opciones de menú.
 *
 * @function useCustomerOrderStatusUI
 * @param {Object} params
 * @param {Object} params.order - Objeto de orden
 * @param {Function} params.updateOrderStatus - Acción para actualizar estado
 * @returns {Object} UI state y handlers
 */
export default function useCustomerOrderStatusUI({ order, updateOrderStatus }) {
  const { t } = useTranslation('InboxMC');

  const confirmAlertRef = useRef(null);
  const [alertTitle, setAlertTitle] = useState('');
  const [newStatus, setNewStatus] = useState('');

  // Mensaje de alerta traducido
  const alertMessage = t('orderStatusSelector.alertMessage');

  // Opciones de estado de orden con clases y handlers
  const orderStatesOptionsMap = useMemo(() => ([
    {
      value: 'pending',
      label: t('orderStatusSelector.status.pending'),
      containerClassName: '!tw-bg-yellow-50 !tw-text-yellow-700',
      onClick: () => handleChangeStatus('pending'),
    },
    {
      value: 'success',
      label: t('orderStatusSelector.status.success'),
      containerClassName: '!tw-bg-green-50 !tw-text-green-700',
      onClick: () => handleChangeStatus('success'),
    },
    {
      value: 'canceled',
      label: t('orderStatusSelector.status.canceled'),
      containerClassName: '!tw-bg-red-50 !tw-text-red-700',
      onClick: () => handleChangeStatus('canceled'),
    },
  ]), [t]);

  // Opciones según estado actual
  const orderStatusOptions = useMemo(() => {
    switch (order.status) {
      case 'pending':
        return orderStatesOptionsMap.slice(1);
      case 'success':
        return orderStatesOptionsMap.slice(2);
      default:
        return [];
    }
  }, [order?.status, orderStatesOptionsMap]);

  // Label del estado actual
  const orderStatusLabel = useMemo(() => {
    const found = orderStatesOptionsMap.find(option => option.value === order.status);
    return found ? found.label : '';
  }, [order?.status, orderStatesOptionsMap]);

  // Clase de estado actual
  const containerClassNameStatus = useMemo(() => {
    const found = orderStatesOptionsMap.find(option => option.value === order.status);
    return found ? found.containerClassName : '';
  }, [order.status, orderStatesOptionsMap]);

  // Handler para abrir alerta de confirmación
  const handleChangeStatus = useCallback((status) => {
    let text;
    switch (status) {
      case 'success':
        text = t('orderStatusSelector.confirm.success');
        break;
      case 'canceled':
        text = t('orderStatusSelector.confirm.canceled');
        break;
      default:
        text = '';
    }
    setAlertTitle(t('orderStatusSelector.confirm.title', { action: text }));
    setNewStatus(status);
    confirmAlertRef.current?.toggleAlert();
  }, [t]);

  // Handler para confirmar cambio
  const handleConfirm = useCallback(() => {
    updateOrderStatus(newStatus);
  }, [updateOrderStatus, newStatus]);

  // Handler para cancelar
  const handleCancel = useCallback(() => {
    confirmAlertRef.current?.toggleAlert();
  }, []);

  return {
    confirmAlertRef,
    alertTitle,
    alertMessage,
    orderStatusLabel,
    orderStatusOptions,
    containerClassNameStatus,
    handleChangeStatus,
    handleConfirm,
    handleCancel
  };
}
